import { mask } from "@/lib/Mask";
import pluralize from "pluralize";
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import updateLocale from 'dayjs/plugin/updateLocale'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import duration from 'dayjs/plugin/duration'
import isBetween from 'dayjs/plugin/isBetween'
import relativeTime from 'dayjs/plugin/relativeTime'
import { EventBus, EVENTS } from "@/lib/EventBus";

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(localizedFormat)
dayjs.extend(updateLocale)
dayjs.extend(advancedFormat)
dayjs.extend(duration)
dayjs.extend(isBetween)
dayjs.extend(relativeTime)

const en24 = {
  ...dayjs.Ls.en,
  name: 'en24',
  formats: {
    'LTS': 'HH:mm:ss',
    'LT': 'HH:mm',
    'L': 'MM/DD/YYYY',
    'LL': 'MMMM D, YYYY',
    'LLL': 'MMMM D, YYYY HH:mm',
    'LLLL': 'dddd, MMMM D, YYYY HH:mm',
    'lll': 'D MMM, YYYY HH:mm:ss',
    'llll': 'ddd, MMM D, YYYY HH:mm:ss',
  }
}
dayjs.locale(en24, null, true)
dayjs.updateLocale('en', {
  formats: {
    'lll': 'D MMM, YYYY h:mm:ss A',
  }
})


let instance = null
let timeZone = null
let timeFormat = null

const _helpers = function() {
  var $this = this.config.globalProperties;
  return {
    passwordRegex: /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-.]).{10,}$/,
    passwordValidationMessage: 'Password must be at least 10 characters long and include uppercase letter, lowercase letter, digit and special character.',
    redirectWithMessage: ({ to, messageText, messageType, duration }) => {
      $this.$router.push(to)
      $this.$buefy.toast.open({
        message: messageText,
        type: messageType || 'is-success',
        duration: duration || 4000
      })
    },
    getDate({ date, withoutTimezone = false} = {}) {
      let m = dayjs(date || undefined).locale( timeFormat == 24 ? 'en24' : 'en')
      if (!withoutTimezone && timeZone) {
        m = m.tz(timeZone)
      }
      return m
    },
    formatDate(date, format = 'lll', withoutTimezone = false) {
      return this.getDate({ date, withoutTimezone }).format(format)
    },
    /**
     * Capitalize first char
     * @param {string} str
     * @returns {string}
     */
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
    /**
     * Returns css class to be identify specific monitoring status.
     * @param {string} status
     * @return {string}
     */
    styleForMonitoringStatus(status) {
      const classes = {
        'stopped': 'is-danger',
        'pending': 'is-info',
        'active': 'is-success',
        'paused': 'is-warning'
      }
      return classes[status] || ''
    },
    debounce,
    downloadBlob(blob, name) {
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = name;
      document.body.appendChild(link);
      link.dispatchEvent(
        new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window
        })
      );
      document.body.removeChild(link);
    },
    /**
     * Audited generated revisions array includes full object as a first element.
     * Every other object records only changes from previous one.
     * This functions goes through the provided array and applies changes on top of each other
     * as a result, instead of individual changes, we get full object state for each revision.
     *
     * @param {array} revisions Audited gem's generated revisions array
     * @returns {*[]}
     */
    modifyRevisionsInPlace(revisions) {
      const modifiedRevisions = []
      for (let i = 0; i < revisions.length; i++) {
        let currentRev = revisions[i]
        if (currentRev.action == 'create') {
          modifiedRevisions.push({...currentRev.audited_changes, username: currentRev.username, edited_at: currentRev.created_at })
        } else {
          const newRevision = {...modifiedRevisions[i - 1]}
          for (const [field, values] of Object.entries(currentRev.audited_changes)) {
            newRevision[field] = values[1]
          }
          newRevision.username = currentRev.username
          newRevision.edited_at = currentRev.created_at
          modifiedRevisions.push(newRevision)
        }
      }
      // last element becomes most updated state which we probably already display in UI
      modifiedRevisions.pop()
      return modifiedRevisions.reverse() // reversing array to have eary updates at the top
    },
    sanitizeInputForRegExp(input) {
      if (!input) {
        return
      }

      return String(input).replace(/[/\-\\^$*+?.()|[\]{}]/g, '\\$&')
    },
    mask(type, value) {
      return mask(type, value, $this.$store.getters['UserPreferencesStore/getPreference']('mask_phi')?.enabled)
    },
    pluralize,
    humanize(str) {
      if (!str) {
        return
      }
      return str.split(/[_-]/)
        .map(word => this.capitalize(word))
        .join(' ');
    },
    bytesToMb(bytes) {
      return (bytes / (1024 * 1024)).toFixed(2)
    },
    getTimeDifference(date) {
      if (!date) {
        return
      }
      const now = this.getDate({ date: new Date() })
      const lastSeen = this.getDate({ date })
      return now.diff(lastSeen, 'hours')
    },
    getHumanTimeDifference(date, fallback, withoutSuffix = false) {
      if (!date) {
        return fallback
      }
      const lastSeen = this.getDate({ date })
      return lastSeen.fromNow(withoutSuffix)
    },
    once,
    openPatientQuickEditModal(patientId) {
      EventBus.$emit(EVENTS.OPEN_PATIENT_QUICK_EDIT_MODAL, patientId);
    },
    getMonitoringSource(route) {
      const routeName = $this.$route.name || route
      
      if (routeName === 'infinity-watch') {
        return 'infinity_watch'
      } else if (routeName === 'patient-central.team-mode') {
        return 'team_mode'
      } else if (routeName === 'patient-central') {
        return 'central_monitoring'
      }
    
      return ''
    },
  }
}

export const once = (fn) => {
  let called = false;
  let result;
  
  const wrapper = function(...args) {
    if (!called) {
      called = true;
      result = fn.apply(this, args);
    }
    return result;
  };
  
  wrapper.reset = function() {
    called = false;
    result = undefined;
  };
  
  return wrapper;
};


export const _dayjs = dayjs;
const getHelpers = function(vuePrototype) {
  if (instance == null) {
    instance = _helpers.bind(vuePrototype)()
  }
  return instance
}

// https://github.com/you-dont-need/You-Dont-Need-Lodash-Underscore#_debounce
export const debounce = (func, wait, immediate) => {
  var timeout;
  const debounced = function() {
    var context = this, args = arguments;
    clearTimeout(timeout);
    timeout = setTimeout(function() {
      timeout = null;
      if (!immediate) func.apply(context, args);
    }, wait);
    if (immediate && !timeout) func.apply(context, args);
  };
  debounced.cancel = () => {
    clearTimeout(timeout);
    timeout = null;
  };
  return debounced;
}

export default {
  install: function(app, { store }) {
    store.watch(state => {
      return [state.currentUser, state.divisionId]
    }, ([currentUser, divisionId]) => {
      if (currentUser && divisionId) {
        timeZone   = currentUser.timezone
        timeFormat = currentUser.time_format
      }
    }, { immediate: true })
    app.config.globalProperties['$helpers'] = getHelpers(app)
  }
}
